import { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck, faClockRotateLeft, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { money, percent, useFinancialVisible } from '@frontend/BidSheets/financialDisplay';
import type { BidVsActual } from '@frontend/AppSettings/services/PipelineProjectService';

/**
 * The BvA trend family (residual gap A1): the demo's cumulative cost chart — Planned (dashed) /
 * Actual (filled) / Projected (dashed) under flat Budgeted-Cost and Contract-Ceiling lines —
 * with the red/green drift banner, the "replay the job" day scrubber and the projected-margin
 * gauge, re-implemented over the server's `series` (the demo files are reference, never source).
 *
 * ONE OWNER of the projection arithmetic, and it is not this file:
 *  - every `eac` figure comes from `series[d].eac`, computed server-side through the same rule
 *    as `totals.projected` (the last day's eac IS totals.projected — pinned by the backend).
 *    The scrubber's projection line only INTERPOLATES between two payload figures, which is
 *    presentational, like drawing the line segment itself.
 *  - the only other client arithmetic is the drift comparison (two payload totals against the
 *    demo's own 1% threshold) and the planned-margin division for the banner's erosion clause
 *    ((contract − planned) ÷ contract — two payload figures, sanctioned by the plan).
 *
 * Money discipline: the parent renders this only for a permitted caller (the route 403s blind
 * ones), and every FIGURE — banner, gauge, axis ticks, tooltip — formats through
 * `money()`/`percent()`, so nothing paints a number before the capability resolves.
 *
 * chart.js registration is global and idempotent; BidDashboard registers Bar/Arc, this module
 * adds Line/Point/Filler ADDITIVELY off the same top-level dep — no second chart.js instance.
 */
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const num = (v: string | null | undefined): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

interface BidVsActualTrendProps {
  /** The permitted BvA payload — the SAME ['BidVsActual', matterid] query the KPI cards read;
   * this component must never fetch (one fetch, one figure — the A6 coordination rule). */
  bva: BidVsActual;
}

const BidVsActualTrend = ({ bva }: BidVsActualTrendProps): JSX.Element | null => {
  const visible = useFinancialVisible();   // re-render (and re-tick the chart) when money resolves
  const series = bva.series ?? [];
  const last = series.length - 1;
  // null = the series' end ("today" in data terms) — where the invariant makes the chart and
  // the table tell one story. Clamped so a refetched shorter series cannot strand the thumb.
  const [scrub, setScrub] = useState<number | null>(null);
  const day = scrub === null ? last : Math.min(scrub, last);

  const planned = num(bva.totals.planned);
  const projected = num(bva.totals.projected);
  const contract = num(bva.margin?.contract);
  const projMarginPct = num(bva.margin?.projected_margin_pct);
  const netFaMarginPct = num(bva.margin?.projected_margin_pct_net_fa);
  const faBillable = num(bva.margin?.fa_billable);
  const targetPct = num(bva.margin?.target_pct);

  // The demo's own threshold (plan decision c): drifting when projected exceeds planned by >1%
  // — equivalently totals.projected_variance < −1% × planned. No projection → NO banner: "on
  // track" over no data is a claim, the honest state is silence. Same for a ZERO plan (review):
  // any spend over nothing planned is unbounded drift, and a green "within 1% of plan" over it
  // would be a lie — with no plan to track, the banner has nothing honest to say either way.
  const hasBanner = planned !== undefined && planned > 0 && projected !== undefined;
  const drifting = planned !== undefined && planned > 0 && projected !== undefined
    && projected - planned > planned * 0.01;
  // The payload's own figure, sign-flipped (planned − projected, positive = under-run) — never
  // a second derivation of a server-owned number.
  const projectedVariance = num(bva.totals.projected_variance);
  const overrun = projectedVariance === undefined ? undefined : -projectedVariance;
  // Presentational division over two payload figures (plan decision c) — the bid's margin had
  // the projection landed on plan. NOT an owner of margin arithmetic.
  const plannedMarginPct = contract !== undefined && contract > 0 && planned !== undefined
    ? ((contract - planned) / contract) * 100 : undefined;

  const lineData = useMemo(() => {
    const anchor = num(series[day]?.actual_cum) ?? 0;
    const eacAtDay = num(series[day]?.eac);
    const steps = last - day;
    const datasets = [
      {
        label: 'Planned Cost',
        data: series.map((p) => num(p.planned_cum) ?? null),
        borderColor: '#0ea5e9',
        backgroundColor: 'rgba(14,165,233,0.05)',
        borderWidth: 2.5,
        borderDash: [6, 4],
        pointRadius: 0,
        tension: 0.25,
        fill: false,
      },
      {
        label: 'Actual Cost',
        // The replay truncation: the SERVER's cumulative actuals up to the scrubbed day,
        // nulled after it — never re-summed here.
        data: series.map((p, i) => (i <= day ? num(p.actual_cum) ?? null : null)),
        borderColor: '#dc2626',
        backgroundColor: 'rgba(220,38,38,0.08)',
        borderWidth: 3,
        pointRadius: 2,
        pointBackgroundColor: '#dc2626',
        tension: 0.25,
        fill: true,
        spanGaps: false,
      },
      {
        label: 'Projected at Completion',
        // Re-aimed by the scrubbed day's OWN eac: a straight segment from the actual line's
        // endpoint to series[day].eac at the series' end. Interpolation only — the landing
        // figure is the server's.
        data: series.map((_, i) => {
          if (i < day || eacAtDay === undefined) return null;
          if (steps <= 0) return eacAtDay;
          return anchor + ((eacAtDay - anchor) * (i - day)) / steps;
        }),
        borderColor: '#d97706',
        backgroundColor: 'rgba(217,119,6,0.04)',
        borderWidth: 2.5,
        borderDash: [3, 3],
        pointRadius: 0,
        tension: 0.25,
        fill: false,
        spanGaps: true,
      },
      {
        label: 'Budgeted Cost',
        data: series.map(() => planned ?? null),
        borderColor: '#64748b',
        borderWidth: 1.5,
        pointRadius: 0,
        fill: false,
      },
      // No contract value → no ceiling line — an absent figure is never drawn as $0.
      ...(contract !== undefined ? [{
        label: 'Contract Ceiling',
        data: series.map(() => contract as number | null),
        borderColor: '#1e293b',
        borderWidth: 1.5,
        pointRadius: 0,
        fill: false,
      }] : []),
    ];
    return { labels: series.map((p) => p.date.slice(5)), datasets };
    // `visible` is a REAL dependency the linter cannot see: money()/percent() read the module
    // flag it mirrors, and a memoized identity would leave the chart un-redrawn (ticks still
    // '—') when the capability resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, day, last, planned, contract, visible]);

  const lineOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    animation: { duration: 250 },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(226,232,240,0.5)' },
        ticks: {
          font: { size: 11 },
          color: '#64748b',
          // Through money(), deliberately: the axis redacts with everything else until the
          // capability resolves — a tick is a figure too.
          callback: (v: unknown) => money(Number(v)),
        },
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: '#64748b' },
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { font: { size: 11 }, usePointStyle: true, pointStyleWidth: 10, padding: 14 },
      },
      tooltip: {
        callbacks: {
          label: (ctx: { parsed: { y: number | null }; dataset: { label?: string } }) => (
            ctx.parsed.y === null ? '' : ` ${ctx.dataset.label}: ${money(ctx.parsed.y)}`),
        },
      },
    },
    // Same as lineData: the tick/tooltip formatters must re-run when money resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [visible]);

  if (series.length === 0) return null;

  // Gauge tones: losing money is red regardless; below the bid's target is amber; at or above
  // is green. With NO target the value renders alone — a band is never invented (plan e).
  const gaugeTone = projMarginPct === undefined ? ''
    : projMarginPct < 0 ? 'red'
      : targetPct !== undefined && projMarginPct < targetPct ? 'amber' : 'green';
  const gaugeFill = projMarginPct === undefined ? 0
    : Math.max(0, Math.min(100,
      targetPct !== undefined && targetPct > 0
        ? (projMarginPct / targetPct) * 100 : projMarginPct));

  return (
    <div className="pipeline-bva-trend">
      {hasBanner && (drifting ? (
        <div className="pipeline-bva-alert red" role="status">
          <FontAwesomeIcon icon={faTriangleExclamation} className="pipeline-bva-alert-icon" />
          <div>
            <div className="pipeline-bva-alert-title">
              The projection has left the bid — you will lose margin unless you act
            </div>
            <div className="pipeline-bva-alert-text">
              At the current burn rate this job projects to finish at{' '}
              <strong>{money(projected)}</strong> — about{' '}
              <strong>{money(overrun)} over the plan</strong>
              {plannedMarginPct !== undefined && projMarginPct !== undefined && (
                <>, cutting margin from {percent(plannedMarginPct)} to {percent(projMarginPct)}</>
              )}
              .
              {faBillable !== undefined && faBillable > 0 && netFaMarginPct !== undefined && (
                <>
                  {' '}Net of billable Force Account, the projected margin recovers
                  to {percent(netFaMarginPct)}.
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="pipeline-bva-alert green" role="status">
          <FontAwesomeIcon icon={faCircleCheck} className="pipeline-bva-alert-icon" />
          <div>
            <div className="pipeline-bva-alert-title">The job is tracking the bid</div>
            <div className="pipeline-bva-alert-text">
              Projected cost is within 1% of plan. Drag the day slider to replay how the
              projection developed.
            </div>
          </div>
        </div>
      ))}

      <div className="pipeline-bva-trend-grid">
        <div className="pipeline-bva-chart">
          <Line data={lineData} options={lineOptions} />
        </div>
        {projMarginPct !== undefined && (
          <div className="pipeline-gauge">
            <div className={`pipeline-gauge-value ${gaugeTone}`}>{percent(projMarginPct)}</div>
            <div className="pipeline-gauge-label">Projected margin</div>
            <div className="pipeline-bar-track">
              <div
                className={`pipeline-bar-fill ${gaugeTone}`}
                style={{ width: `${gaugeFill}%` }}
              />
              {targetPct !== undefined && <div className="pipeline-bar-marker" />}
            </div>
            <div className="pipeline-gauge-target">
              {targetPct !== undefined ? <>Target {percent(targetPct)}</> : 'No margin target set'}
            </div>
          </div>
        )}
      </div>

      {series.length > 1 && (
        <div className="pipeline-bva-scrub">
          <div className="pipeline-bva-scrub-head">
            <span className="pipeline-bva-scrub-title">
              <FontAwesomeIcon icon={faClockRotateLeft} /> Replay the job
            </span>
            <span className="pipeline-bva-scrub-value">
              Day {day + 1} of {series.length} · {series[day].date}
            </span>
          </div>
          <input
            type="range"
            className="pipeline-bva-slider"
            min={0}
            max={last}
            step={1}
            value={day}
            aria-label="Replay day"
            onChange={(e) => setScrub(Number(e.target.value))}
          />
        </div>
      )}
    </div>
  );
};

export default BidVsActualTrend;
